import React from 'react';

import { useLocation } from 'react-router';

import { Nullable } from '@clearverify/types';

import { SortOption } from 'src/types/sort';
import { createOrderByQueryVariable } from 'src/utils/gql';

import { FilterStorageContext } from './FilterStorageContext';
import type { FilterItem, FilterStorageContextValue } from './types';
import { useFilterStateStorage, usePaginationStateStorage, useSortStateStorage } from './utils';

export const FilterStorageProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { pathname } = useLocation();
  const { filterStorage, getFilterByKey, setFilterByKey, resetFilterStorage } =
    useFilterStateStorage();
  const { sortOptionStorage, getSortOptionByKey, setSortOptionByKey, resetSortOptionStorage } =
    useSortStateStorage();
  const {
    paginationStorage,
    currentPagination,
    currentPaginationQueryVariables,
    getPaginationObjectByKey,
    getPaginationQueryVariablesByKey,
    setPaginationByKey,
    getPaginationByKey,
    resetPaginationStorage,
  } = usePaginationStateStorage();

  // Filter By Location

  const currentFilterByLocation = React.useMemo(() => {
    return getFilterByKey(pathname);
  }, [getFilterByKey, pathname]);

  const setCurrentFilter = React.useCallback(
    (filter: FilterItem) => {
      setFilterByKey(pathname, filter);
    },
    [pathname, setFilterByKey],
  );

  // Sort By Location

  const currentSortByLocation = React.useMemo(() => {
    return getSortOptionByKey(pathname);
  }, [getSortOptionByKey, pathname]);

  const currentSortQueryVariableByLocation = React.useMemo(() => {
    return createOrderByQueryVariable(currentSortByLocation);
  }, [currentSortByLocation]);

  const setCurrentSortOption = React.useCallback(
    (sortOption: Nullable<SortOption>) => {
      setSortOptionByKey(pathname, sortOption);
    },
    [pathname, setSortOptionByKey],
  );

  const getQueryParamsByKey = React.useCallback(
    (key: string) => {
      return {
        filter: filterStorage[key] || {},
        sort: sortOptionStorage[key] || null,
      };
    },
    [filterStorage, sortOptionStorage],
  );

  const resetStorage = React.useCallback(() => {
    resetFilterStorage();
    resetSortOptionStorage();
    resetPaginationStorage();
  }, [resetFilterStorage, resetPaginationStorage, resetSortOptionStorage]);

  const contextValue = React.useMemo((): FilterStorageContextValue => {
    return {
      filterStorage,
      getFilterByKey,
      setFilterByKey,
      sortOptionStorage,
      getSortOptionByKey,
      setSortOptionByKey,
      paginationStorage,
      getPaginationByKey,
      setPaginationByKey,
      filter: currentFilterByLocation,
      setFilter: setCurrentFilter,
      sortOption: currentSortByLocation,
      sortQueryVariable: currentSortQueryVariableByLocation,
      setSortOption: setCurrentSortOption,
      pagination: currentPagination,
      paginationQueryVariables: currentPaginationQueryVariables,
      getPaginationObjectByKey,
      getPaginationQueryVariablesByKey,
      getQueryParamsByKey,
      resetStorage,
    };
  }, [
    currentFilterByLocation,
    currentPagination,
    currentPaginationQueryVariables,
    currentSortByLocation,
    currentSortQueryVariableByLocation,
    filterStorage,
    getFilterByKey,
    getPaginationByKey,
    getPaginationObjectByKey,
    getPaginationQueryVariablesByKey,
    getQueryParamsByKey,
    getSortOptionByKey,
    paginationStorage,
    setCurrentFilter,
    setCurrentSortOption,
    setFilterByKey,
    setPaginationByKey,
    setSortOptionByKey,
    sortOptionStorage,
    resetStorage,
  ]);

  return <FilterStorageContext value={contextValue}>{children}</FilterStorageContext>;
};
